/**
 * Display formatters shared by the Insights views and the sidebar.
 * Compact by design — these sit in 11–12px table cells.
 */

/** 1234 → "1,234" */
export function formatInt(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return Math.round(n).toLocaleString("en-GB");
}

/** 3_900_000 → "3.9M", 10_900 → "10.9k", 812 → "812" */
export function formatTokens(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `${trim(n / 1_000_000_000)}B`;
  if (abs >= 1_000_000) return `${trim(n / 1_000_000)}M`;
  if (abs >= 1_000) return `${trim(n / 1_000)}k`;
  return String(Math.round(n));
}

function trim(v: number): string {
  const fixed = v >= 100 ? v.toFixed(0) : v.toFixed(1);
  return fixed.endsWith(".0") ? fixed.slice(0, -2) : fixed;
}

/** 48_200 → "48s", 252_000 → "4m 12s", 3_725_000 → "1h 02m", 30_600 → "30.6s" */
export function formatDurationMs(
  ms: number | null | undefined,
  opts: { precise?: boolean } = {},
): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return "—";
  const s = ms / 1000;
  if (s < 60) return opts.precise && s < 10 ? `${s.toFixed(1)}s` : s < 60 && opts.precise ? `${s.toFixed(1)}s` : `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  if (m < 60) {
    const rem = Math.round(s - m * 60);
    return rem ? `${m}m ${String(rem).padStart(2, "0")}s` : `${m}m`;
  }
  const h = Math.floor(m / 60);
  const remM = m - h * 60;
  return remM ? `${h}h ${String(remM).padStart(2, "0")}m` : `${h}h`;
}

/** 764_640_000 ms → "212.4h" — total agent time as hours with one decimal. */
export function formatHours(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return "—";
  const h = ms / 3_600_000;
  if (h < 1) return `${Math.round(ms / 60_000)}m`;
  return `${h >= 100 ? h.toFixed(0) : h.toFixed(1)}h`;
}

/** 94.2 → "94.2%", 100 → "100%" */
export function formatPercent(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return Number.isInteger(v) ? `${v}%` : `${v.toFixed(1)}%`;
}

/**
 * Cost in cents (USD — model_catalogue prices are dollar cents) → "$412.60".
 * Sub-cent values keep two decimals so a cheap run isn't shown as "$0".
 */
export function formatCostCents(cents: number | null | undefined, opts: { compact?: boolean } = {}): string {
  if (cents == null || !Number.isFinite(cents)) return "—";
  const dollars = cents / 100;
  if (opts.compact && dollars >= 1000) return `$${(dollars / 1000).toFixed(1)}k`;
  if (dollars > 0 && dollars < 0.01) return "<$0.01";
  return `$${dollars.toFixed(2)}`;
}

/** "2h ago", "4m ago", "3d ago", "just now" */
export function formatRelativeTime(iso: string | Date | null | undefined): string {
  if (!iso) return "—";
  const t = typeof iso === "string" ? new Date(iso).getTime() : iso.getTime();
  const diff = Date.now() - t;
  if (!Number.isFinite(diff)) return "—";
  if (diff < 45_000) return "just now";
  if (diff < 3_600_000) return `${Math.max(1, Math.round(diff / 60_000))}m ago`;
  if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)}h ago`;
  if (diff < 30 * 86_400_000) return `${Math.round(diff / 86_400_000)}d ago`;
  return new Date(t).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

/** "Today 09:41", "Yesterday 17:20", "2 Sep 08:58" */
export function formatStartedAt(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  if (sameDay(d, now)) return `Today ${time}`;
  if (sameDay(d, yesterday)) return `Yesterday ${time}`;
  return `${d.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} ${time}`;
}

/** "6 Aug – 4 Sep" */
export function formatDateRange(sinceIso: string, untilIso: string): string {
  const f = (iso: string) =>
    new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  return `${f(sinceIso)} – ${f(untilIso)}`;
}

/** "Tue 2 Sep" for chart tooltips. */
export function formatDayLabel(dateKey: string): string {
  const d = new Date(`${dateKey}T00:00:00Z`);
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });
}

/** "+12%" / "-8%" with sign. */
export function formatSignedPercent(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${v > 0 ? "+" : ""}${v}%`;
}
